import { BaseLogo, ImageRef } from "../../../../core/models/entities/BaseLogo";
import { isBlankDataUri } from "../../../../hooks/useHealPendingHighRes";

// Flag REACT_APP_LOGO_KEEP_SHARP - pure helpers for the rule "never show the
// 180px thumbnail when something sharp exists" (F1 never downgrade, F2 upload
// keeps the local file, F3 clone opens with the source's high-res). Nothing
// here reads the flag: the call sites branch on it, so with the flag off none
// of this runs.

/**
 * A bitmap worth keeping on the canvas: a high-res URL that is neither the
 * low-res fallback the mapper substitutes for a missing high-res nor the
 * API's blank data-URI. Deliberately ignores highResPending - a logo seeded
 * with a sharp PLACEHOLDER (the upload's own file, a clone's source render)
 * carries the pending mark so the heal keeps polling for the logo's own
 * render, and still shows a sharp bitmap.
 */
export function isSharpBitmap(image: ImageRef | undefined): boolean {
  if (!image || !image.highResUrl) return false;
  if (image.highResUrl === image.lowResUrl) return false;
  return !isBlankDataUri(image.highResUrl);
}

/**
 * The server's own genuine high-res for THIS logo: a sharp bitmap that is
 * not marked pending. Only such an image may replace a sharp one.
 */
export function hasGenuineHighRes(image: ImageRef | undefined): boolean {
  return isSharpBitmap(image) && image!.highResPending !== true;
}

/**
 * F1: the image to show once `incoming` (a mutation response or a heal poll)
 * arrives while `shown` is on the canvas. `shown` survives when it is sharp
 * and `incoming` carries no genuine high-res; otherwise `incoming` wins
 * exactly as today. Returns one of the two references untouched, so callers
 * can compare by identity.
 */
export function keepSharpImage(
  shown: ImageRef | undefined,
  incoming: ImageRef | undefined
): ImageRef | undefined {
  if (isSharpBitmap(shown) && !hasGenuineHighRes(incoming)) return shown;
  return incoming;
}

/**
 * F1 for a mutation response that does not change the artwork (resize,
 * title, customer): the response with the sharp bitmap the user is looking
 * at kept in place of the response's 180px thumbnail. Everything else in the
 * response (mm, product, colours) is taken as is. Another logo, no shown
 * logo or a response that carries a genuine high-res = the response itself.
 */
export function withKeptSharpImage<T extends BaseLogo>(
  shown: BaseLogo | null | undefined,
  response: T
): T {
  if (!shown || shown.id !== response.id) return response;
  const image = keepSharpImage(shown.displayImage, response.displayImage);
  if (image === response.displayImage) return response;
  return { ...response, displayImage: image as ImageRef };
}

/**
 * F2/F3: `logo` with `sharpUrl` as its canvas bitmap while its own high-res
 * is still pending. `markPending` stamps highResPending so the heal polls
 * for the logo's own render (useHealPendingHighRes honours the mark under
 * REACT_APP_LOGO_HEAL_PER_MUTATION); false keeps the mapper's own value
 * (used when a later mutation response is known to re-seed the entry).
 * No image or no URL = the logo untouched. Whether the logo's OWN image is
 * worth keeping is the caller's call: the upload checks hasGenuineHighRes
 * first, while the copy response's highResUrl is known to be the 180px
 * processed blob (LogoFunctions.cs CopyLogo) and is always overlaid.
 */
export function withSharpPlaceholder<T extends BaseLogo>(
  logo: T,
  sharpUrl: string | undefined,
  markPending: boolean
): T {
  if (!sharpUrl || !logo.displayImage) return logo;
  const displayImage: ImageRef = markPending
    ? { ...logo.displayImage, highResUrl: sharpUrl, highResPending: true }
    : { ...logo.displayImage, highResUrl: sharpUrl };
  return { ...logo, displayImage };
}

/**
 * F3: the source's sharp bitmap for a clone, if any - the first candidate
 * with a genuine high-res wins (the detail cache entry the popup filled
 * first, the list/popup object second).
 */
export function sourceSharpUrl(
  ...candidates: (BaseLogo | null | undefined)[]
): string | undefined {
  for (const candidate of candidates) {
    if (candidate && hasGenuineHighRes(candidate.displayImage)) {
      return candidate.displayImage.highResUrl;
    }
  }
  return undefined;
}
